// Szegmens-szerkesztő (UI-4): a new segment, or one already saved.
//
// Left, the definition: condition groups joined by ÉS, alternatives by VAGY,
// each row in Klaviyo's words; for a new segment, the warmup waves and the
// common Klaviyo segments as one-click starting points. Right, what the
// definition means right now: the live count (with what a send would leave out
// and why, and ten matching contacts), and for a saved segment its state —
// the build of a static one as the `segments run` loop moves it, the last
// count of a dynamic one — and the campaigns that use it.
//
// Saving a static segment asks for its build straight away (the state
// machine's request path); saving a dynamic one counts it, so the list shows
// a number with the moment it was taken.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArchiveIcon, CopyIcon, MapPinIcon, SaveIcon } from "lucide-react";
import {
  archiveSegment,
  countSegment,
  createSegment,
  fetchSegment,
  fetchSegmentUsage,
  materialiseSegment,
  updateSegment,
} from "@/lib/api";
import type { Definition, Segment, SegmentDetail, SegmentKind, SegmentUse } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { ActionDialog } from "@/components/emc/ActionDialog";
import { DetailList } from "@/components/emc/DetailList";
import { Field } from "@/components/emc/Field";
import { PageHeader } from "@/components/emc/PageHeader";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout, EmptyState, ErrorState, LoadingState } from "@/components/emc/states";
import { minute } from "@/components/emc/format";
import { labelOf } from "@/components/emc/status";
import { usePageCrumb } from "@/components/emc/shell/crumb";
import { cn } from "@/lib/utils";
import { ConditionsEditor } from "@/pages/segments/ConditionsEditor";
import { LiveCountPanel, useLiveCount } from "@/pages/segments/LiveCount";
import { Presets } from "@/pages/segments/Presets";
import { SegmentStatus } from "@/pages/segments/SegmentStatus";
import { COUNTRIES, messageOf, useBuilderEnv, useCatalogue } from "@/pages/segments/data";
import { EngineError, engineLine } from "@/pages/segments/EngineError";
import { forgetMemberships, fromDefinition, opaqueCount, toDefinition } from "@/pages/segments/model";
import type { Assembled, BuilderDraft } from "@/pages/segments/model";
import { useLeaveGuard } from "@/pages/segments/useLeaveGuard";
import { KlaviyoSegmentView } from "@/pages/segments/KlaviyoSegmentView";
import { isKlaviyo } from "@/pages/segments/klaviyo";

const POLL_MS = 2_000;
const BUILDING = new Set(["requested", "materialising"]);
// After a save, these ask for a build; `requested` already has one coming.
const REBUILDABLE = new Set(["defined", "ready", "stale", "failed"]);

/** JSON with its keys sorted, so "unchanged" does not depend on key order. */
function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, item: unknown) =>
    item !== null && typeof item === "object" && !Array.isArray(item)
      ? Object.fromEntries(Object.entries(item as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)))
      : item,
  );
}

const WAITING: Assembled = { ok: false, errors: {}, general: "A feltétel-lista betöltése…" };

export default function SegmentBuilder({ segmentId }: { segmentId: number | null }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const copyOf = segmentId === null ? Number(params.get("masolat")) || null : null;
  const { catalogue, error: catalogueError } = useCatalogue();

  const [detail, setDetail] = useState<SegmentDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [country, setCountry] = useState(() => {
    const asked = (params.get("orszag") ?? "").toUpperCase();
    return COUNTRIES.includes(asked) ? asked : "HU";
  });
  const [kind, setKind] = useState<SegmentKind>("dynamic");
  const [draft, setDraft] = useState<BuilderDraft>({ items: [] });
  const [baseline, setBaseline] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [uses, setUses] = useState<SegmentUse[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const [archiving, setArchiving] = useState(false);
  const [presetsOpen, setPresetsOpen] = useState(segmentId === null);
  const bypass = useRef(false);
  // The version the rows were loaded from. Not `detail`: the build poll
  // refreshes that without touching the rows, and a save has to name the
  // version it was edited from, or it writes over a save made in between.
  const loadedVersion = useRef<number | null>(null);

  const activeCountry = detail?.country ?? country;
  const readOnly = detail?.state === "archived";
  const buildingNow = detail !== null && detail.kind === "static" && detail.state === "materialising";
  const env = useBuilderEnv(activeCountry, segmentId);
  const assembled = useMemo(() => (catalogue ? toDefinition(draft, catalogue) : WAITING), [catalogue, draft]);
  const live = useLiveCount(activeCountry, assembled, draft);
  const names = useMemo(
    () => ({
      lists: new Map(env.lists.map((option) => [String(option.id), option.name])),
      segments: new Map(env.segments.map((option) => [String(option.id), option.name])),
    }),
    [env.lists, env.segments],
  );

  usePageCrumb(segmentId === null ? "Új szegmens" : detail?.name);

  // --- loading ------------------------------------------------------------

  const adopt = useCallback((segment: SegmentDetail) => {
    loadedVersion.current = segment.definition_version;
    setDetail(segment);
    setDraft(fromDefinition(segment.definition));
    setBaseline(canonical(segment.definition));
  }, []);

  useEffect(() => {
    if (segmentId === null) return;
    let live = true;
    setLoadError(null);
    fetchSegment(segmentId)
      .then((segment) => live && adopt(segment))
      .catch((raised: unknown) => live && setLoadError(messageOf(raised)));
    return () => {
      live = false;
    };
  }, [segmentId, adopt]);

  useEffect(() => {
    if (copyOf === null) return;
    let live = true;
    fetchSegment(copyOf)
      .then((segment) => {
        if (!live) return;
        setName(`${segment.name} (másolat)`);
        setCountry(segment.country);
        setKind(segment.kind);
        setDraft(fromDefinition(segment.definition));
        setPresetsOpen(false);
      })
      .catch((raised: unknown) => live && setLoadError(messageOf(raised)));
    return () => {
      live = false;
    };
  }, [copyOf]);

  useEffect(() => {
    if (segmentId === null) return;
    let live = true;
    fetchSegmentUsage(activeCountry)
      .then((answer) => live && setUses(answer))
      .catch(() => live && setUses([]));
    return () => {
      live = false;
    };
  }, [segmentId, activeCountry]);

  // A build in flight: follow it, and let the elapsed time move.
  const building = detail !== null && BUILDING.has(detail.state);
  useEffect(() => {
    if (!building || segmentId === null) return undefined;
    const poll = window.setInterval(() => {
      fetchSegment(segmentId)
        .then((segment) => {
          setDetail(segment);
          if (!BUILDING.has(segment.state)) {
            if (segment.state === "ready") {
              toast.success("A szegmens elkészült", {
                description: `${segment.member_count?.toLocaleString("hu-HU") ?? "—"} tag`,
              });
            } else if (segment.state === "failed") {
              toast.error("Az építés nem sikerült", { description: segment.failure ?? undefined });
            }
          }
        })
        .catch(() => undefined);
    }, POLL_MS);
    const tick = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => {
      window.clearInterval(poll);
      window.clearInterval(tick);
    };
  }, [building, segmentId]);

  // --- unsaved work ---------------------------------------------------------

  const current = assembled.ok ? canonical(assembled.definition) : `invalid:${canonical(draft.items.length)}`;
  // A Klaviyo segment has no conditions to edit (its definition is empty), so
  // nothing on its page can be unsaved.
  const dirty =
    segmentId === null
      ? draft.items.length > 0 || name.trim() !== ""
      : detail !== null && !isKlaviyo(detail) && baseline !== null && current !== baseline;
  const leaveDialog = useLeaveGuard(dirty && !busy, bypass);

  // --- actions --------------------------------------------------------------

  async function reload(id: number) {
    const segment = await fetchSegment(id);
    adopt(segment);
    return segment;
  }

  /** After a save: a static segment's build request, or a dynamic one's
   * count. The save stands either way; a failure here is a sentence to show,
   * not a failed save — the segment's own page can retry it. */
  async function followUp(segment: Segment): Promise<string | null> {
    if (segment.kind === "static") {
      if (!REBUILDABLE.has(segment.state)) return null;
      return materialiseSegment(segment.id).then(
        () => null,
        (raised: unknown) =>
          `Az építést nem sikerült elindítani: ${engineLine(messageOf(raised), names.segments)}`,
      );
    }
    return countSegment(segment.id).then(
      () => null,
      () => null,
    );
  }

  async function save() {
    setShowErrors(true);
    setSaveError(null);
    if (!assembled.ok) {
      setSaveError(assembled.general ?? "Néhány feltétel hiányos — a pirossal jelölt sorokat javítsd.");
      return;
    }
    if (segmentId === null && name.trim() === "") {
      setSaveError("Adj nevet a szegmensnek — a kampány-varázsló ezen a néven kínálja fel.");
      return;
    }
    setBusy(true);
    try {
      if (segmentId === null) {
        const created = await createSegment({ country, name: name.trim(), kind, definition: assembled.definition });
        const warning = await followUp(created);
        if (warning) toast.warning("A szegmens elmentve", { description: warning });
        else {
          toast.success("A szegmens elmentve", {
            description: created.kind === "static" ? "A tagság építése elindult." : created.name,
          });
        }
        bypass.current = true;
        navigate(`/szegmensek/${created.id}`, { replace: true });
        return;
      }
      const saved = await updateSegment(segmentId, assembled.definition, loadedVersion.current);
      const warning = await followUp(saved);
      await reload(segmentId);
      setShowErrors(false);
      if (warning) toast.warning("A szegmens elmentve", { description: warning });
      else {
        toast.success("A szegmens elmentve", {
          description: saved.kind === "static" ? "Új verzió — a tagság újraépül." : "Új verzió, újraszámolva.",
        });
      }
    } catch (raised: unknown) {
      setSaveError(messageOf(raised));
    } finally {
      setBusy(false);
    }
  }

  async function build() {
    if (segmentId === null) return;
    setBusy(true);
    try {
      await materialiseSegment(segmentId);
      await reload(segmentId);
      toast.success("A tagság építése elindult");
    } catch (raised: unknown) {
      toast.error("Az építés nem indult el", { description: engineLine(messageOf(raised), names.segments) });
    } finally {
      setBusy(false);
    }
  }

  async function recount() {
    if (segmentId === null) return;
    setBusy(true);
    try {
      const counted = await countSegment(segmentId);
      await reload(segmentId);
      toast.success("Újraszámolva", { description: `${counted.member_count?.toLocaleString("hu-HU") ?? "—"} kontakt` });
    } catch (raised: unknown) {
      toast.error("A számolás nem sikerült", { description: engineLine(messageOf(raised), names.segments) });
    } finally {
      setBusy(false);
    }
  }

  function pickPreset(definition: Definition, presetName: string) {
    const previous = draft;
    setDraft(fromDefinition(definition));
    if (segmentId === null && name.trim() === "") setName(presetName);
    setShowErrors(false);
    setPresetsOpen(false);
    if (previous.items.length > 0) {
      toast("A minta a korábbi feltételek helyére került", {
        action: { label: "Visszavonás", onClick: () => setDraft(previous) },
      });
    }
  }

  // --- the page ---------------------------------------------------------------

  if (loadError !== null) {
    return (
      <>
        <PageHeader title="Szegmens" back={{ to: "/szegmensek", label: "Szegmensek" }} />
        <ErrorState message={loadError} />
      </>
    );
  }
  if (detail !== null && isKlaviyo(detail)) {
    return <KlaviyoSegmentView segment={detail} />;
  }
  if (catalogueError !== null) {
    return (
      <>
        <PageHeader title="Szegmens" back={{ to: "/szegmensek", label: "Szegmensek" }} />
        <ErrorState message={catalogueError} />
      </>
    );
  }
  if (catalogue === null || (segmentId !== null && detail === null)) {
    return (
      <>
        <PageHeader title="Szegmens" back={{ to: "/szegmensek", label: "Szegmensek" }} />
        <LoadingState />
      </>
    );
  }

  const errors = assembled.ok ? {} : assembled.errors;
  const effectiveKind = detail?.kind ?? kind;
  const saveLabel =
    effectiveKind === "static" && !(detail !== null && detail.state === "requested") ? "Mentés és építés" : "Mentés";
  const activeCampaigns = uses.filter(
    (use) => use.segment_id === segmentId && ["draft", "scheduled", "snapshotting"].includes(use.campaign_state),
  );

  return (
    <>
      <PageHeader
        title={detail?.name ?? "Új szegmens"}
        back={{ to: `/szegmensek?orszag=${activeCountry}`, label: "Szegmensek" }}
        meta={
          detail && (
            <>
              <StatusPill domain="segment" state={detail.state} />
              <span className="text-muted-foreground">{labelOf("segmentKind", detail.kind)}</span>
            </>
          )
        }
        description={
          detail
            ? undefined
            : "Feltételekből álló közönség egy országon belül. A csoportok között ÉS, egy csoporton belül VAGY kapcsolat van; a darabszám minden változtatás után magától frissül."
        }
        actions={
          <>
            {detail && (
              <Button
                variant="outline"
                type="button"
                onClick={() => navigate(`/szegmensek/uj?masolat=${detail.id}`)}
              >
                <CopyIcon />
                Másolat
              </Button>
            )}
            {detail && !readOnly && (
              <Button
                variant="outline-danger"
                type="button"
                disabled={busy || BUILDING.has(detail.state)}
                title={BUILDING.has(detail.state) ? "Építés közben nem archiválható" : undefined}
                onClick={() => setArchiving(true)}
              >
                <ArchiveIcon />
                Archiválás
              </Button>
            )}
            {!readOnly && (
              <Button type="button" disabled={busy || buildingNow || (segmentId !== null && !dirty)} onClick={() => void save()}>
                <SaveIcon />
                {busy ? "Mentés…" : saveLabel}
              </Button>
            )}
          </>
        }
      />

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,var(--emc-panel-width))]">
        <div className="flex min-w-0 flex-col gap-6">
          {saveError !== null && <EngineError message={saveError} names={names.segments} />}
          {readOnly && <Callout>Archivált szegmens: a feltételei csak olvashatók.</Callout>}
          {buildingNow && (
            <Callout tone="warn">
              A tagság most épül a mentett feltételekből — amíg elkészül, a feltételek nem menthetők.
            </Callout>
          )}
          {activeCampaigns.length > 0 && effectiveKind === "static" && dirty && (
            <Callout tone="warn">
              Ezt a szegmenst {activeCampaigns.length} még el nem küldött kampány használja. Mentés után a tagság
              újraépül; amíg el nem készül, ezek a kampányok nem indulhatnak el vele.
            </Callout>
          )}

          {segmentId === null ? (
            <section className="flex flex-col gap-4 rounded-card border border-line bg-surface p-4 shadow-emc">
              <h2>Alapok</h2>
              <div className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                <Field label="Név" htmlFor="segment-name" hint="A kampány-varázsló ezen a néven kínálja fel.">
                  <Input
                    id="segment-name"
                    value={name}
                    maxLength={200}
                    placeholder="pl. 1. hullám — 30 napon belül kattintott"
                    onChange={(event) => setName(event.target.value)}
                  />
                </Field>
                <Field label="Ország" htmlFor="segment-country" hint="Egy szegmens egy ország kontaktjaiból válogat.">
                  <NativeSelect
                    id="segment-country"
                    className="w-full"
                    value={country}
                    onChange={(event) => {
                      setCountry(event.target.value);
                      setDraft((current) => forgetMemberships(current));
                    }}
                  >
                    {COUNTRIES.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
              </div>
              <fieldset className="flex flex-col gap-2">
                <legend className="mb-1.5 font-medium">Típus</legend>
                <div className="grid gap-3 md:grid-cols-2">
                  {(
                    [
                      [
                        "dynamic",
                        "Dinamikus",
                        "A kampány a küldés pillanatában számolja újra — az átvett hullámokhoz is ez kell, hogy egy új hullám bélyege a következő küldésben már számítson.",
                      ],
                      [
                        "static",
                        "Statikus",
                        "Mentéskor lefagyasztjuk a tagságot; csak újraépítéskor változik. Egy egész ország kb. egy perc.",
                      ],
                    ] as const
                  ).map(([value, title, text]) => (
                    <label
                      key={value}
                      className={cn(
                        "flex cursor-pointer items-start gap-3 rounded-card border p-3",
                        kind === value ? "border-primary bg-primary-soft" : "border-line bg-surface hover:bg-surface-2",
                      )}
                    >
                      <input
                        type="radio"
                        name="segment-kind"
                        className="mt-1 accent-primary"
                        checked={kind === value}
                        onChange={() => setKind(value)}
                      />
                      <span className="flex flex-col gap-0.5">
                        <span className="font-medium text-ink">{title}</span>
                        <span className="text-label text-muted-foreground">{text}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </section>
          ) : (
            detail && (
              <DetailList
                columns={2}
                className="rounded-card border border-line bg-surface px-4 shadow-emc"
                items={[
                  { label: "Ország", value: detail.country, mono: true },
                  { label: "Típus", value: labelOf("segmentKind", detail.kind) },
                  { label: "Verzió", value: `${detail.definition_version}.`, mono: true },
                  { label: "Létrehozta", value: `${detail.created_by} · ${minute(detail.created_at)}`, muted: true },
                ]}
              />
            )
          )}

          <section className="flex flex-col gap-4 rounded-card border border-line bg-surface p-4 shadow-emc">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2>Feltételek</h2>
              <span className="inline-flex items-center gap-1.5 text-label text-muted-foreground">
                <MapPinIcon className="size-3.5" />
                Ország: <span className="data-mono text-ink">{activeCountry}</span> — minden feltétel ezen belül
              </span>
            </div>
            {opaqueCount(draft) > 0 && (
              <Callout>
                A szegmens egy része itt nem szerkeszthető formában van — változatlanul megmarad, ha mentesz.
              </Callout>
            )}
            {draft.items.length === 0 && (
              <EmptyState
                bare
                title="Még nincs feltétel."
                description="Adj hozzá egyet, vagy tölts be egy mintát lent — a migráció alatt egy kampány közönsége az „Átvett hullámok (1–N)”."
              />
            )}
            <ConditionsEditor
              draft={draft}
              catalogue={catalogue}
              env={env}
              errors={errors}
              strict={showErrors}
              disabled={readOnly}
              onChange={(next) => setDraft(next)}
            />
            {showErrors && !assembled.ok && assembled.general && (
              <p role="alert" className="text-danger">
                {assembled.general}
              </p>
            )}
          </section>

          {!readOnly && (
            <section className="flex flex-col gap-4 rounded-card border border-line bg-surface p-4 shadow-emc">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-col gap-0.5">
                  <h2>Kezdés mintából</h2>
                  <span className="text-label text-muted-foreground">
                    Egy minta csak kitölti a feltételeket; utána szabadon átírható.
                  </span>
                </div>
                <Button variant="ghost" size="sm" type="button" onClick={() => setPresetsOpen((open) => !open)}>
                  {presetsOpen ? "Elrejtés" : "Minták mutatása"}
                </Button>
              </div>
              {presetsOpen && <Presets onPick={pickPreset} disabled={busy} />}
            </section>
          )}
        </div>

        <aside className="flex min-w-0 flex-col gap-6">
          {detail && (
            <SegmentStatus
              segment={detail}
              uses={uses}
              now={now}
              busy={busy}
              names={names.segments}
              onBuild={() => void build()}
              onCount={() => void recount()}
            />
          )}
          <LiveCountPanel state={live} names={names} />
          {detail && detail.versions.length > 1 && (
            <p className="text-label text-muted-foreground">
              {detail.versions.length} mentett verzió; a kampány a küldéskor rögzíti, melyikkel készült.{" "}
              <Link to={`/szegmensek?orszag=${detail.country}`}>Vissza a listához</Link>
            </p>
          )}
        </aside>
      </div>

      {detail && (
        <ActionDialog
          open={archiving}
          onOpenChange={setArchiving}
          tone="danger"
          title="A szegmens archiválása"
          description="Az archivált szegmens nem szerkeszthető és nem építhető újra, és a kampány-varázsló sem kínálja fel. A már elküldött kampányokat nem érinti. Ha egy másik szegmens hivatkozik rá, előbb azt kell módosítani."
          confirmLabel="Archiválás"
          onConfirm={async () => {
            await archiveSegment(detail.id).catch((raised: unknown) => {
              throw new Error(engineLine(messageOf(raised), names.segments));
            });
            await reload(detail.id);
            toast.success("A szegmens archiválva", { description: detail.name });
          }}
        />
      )}
      {leaveDialog}
    </>
  );
}
